import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { 
  Shield, 
  Heart, 
  Scale, 
  BookOpen, 
  Briefcase,
  ArrowRight,
  Phone
} from 'lucide-react';

const ServicesPage = () => {
  const { t } = useLanguage();

  const services = [
    {
      icon: Shield,
      key: 'housing',
      image: 'https://images.pexels.com/photos/3554424/pexels-photo-3554424.jpeg',
      featured: true,
    },
    {
      icon: Heart,
      key: 'psychological',
      image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=800',
    },
    {
      icon: Scale,
      key: 'legal',
      image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800',
    },
    {
      icon: BookOpen,
      key: 'education',
      image: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800',
    },
    {
      icon: Briefcase,
      key: 'reintegration',
      image: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800',
    },
  ];

  return (
    <div data-testid="services-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 gradient-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="section-label">{t.services.subtitle}</span>
            <h1 
              data-testid="services-title"
              className="font-serif text-4xl sm:text-5xl font-bold text-[#1A252C] mt-4 mb-6"
            >
              {t.services.title}
            </h1>
            <p className="text-lg text-[#546A76] leading-relaxed">
              {t.mission.text}
            </p>
          </div>
        </div>
      </section>

      {/* Bento Grid Services */}
      <section data-testid="services-grid" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Featured Service - Safe Housing */}
          <div className="grid lg:grid-cols-12 gap-8 mb-8">
            <div 
              data-testid="service-housing"
              className="lg:col-span-8 relative rounded-3xl overflow-hidden group"
            >
              <img
                src={services[0].image}
                alt={t.services.housing.title}
                loading="lazy"
                className="w-full h-[400px] object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A252C]/90 via-[#1A252C]/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#D4AF37] flex items-center justify-center">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-white">
                    {t.services.housing.title}
                  </h3>
                </div>
                <p className="text-white/90 text-lg leading-relaxed max-w-xl">
                  {t.services.housing.desc}
                </p>
              </div>
            </div>

            {/* Psychological Support */}
            <div 
              data-testid="service-psychological"
              className="lg:col-span-4 bg-[#4A708B] rounded-3xl p-8 flex flex-col justify-between min-h-[400px]"
            >
              <div>
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mb-6">
                  <Heart className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-serif text-2xl font-bold text-white mb-4">
                  {t.services.psychological.title}
                </h3>
                <p className="text-white/90 leading-relaxed">
                  {t.services.psychological.desc}
                </p>
              </div>
              <div className="mt-6">
                <Link to="/contact">
                  <Button 
                    variant="outline"
                    className="rounded-full border-white text-white hover:bg-white hover:text-[#4A708B] bg-transparent"
                  >
                    {t.common.learnMore}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Second Row */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Legal Assistance */}
            <div 
              data-testid="service-legal"
              className="bg-white rounded-3xl p-8 border border-[#E2E8F0] card-hover"
            >
              <div className="w-14 h-14 rounded-2xl bg-[#4A708B]/10 flex items-center justify-center mb-6">
                <Scale className="w-7 h-7 text-[#4A708B]" />
              </div>
              <h3 className="font-serif text-xl font-bold text-[#1A252C] mb-4">
                {t.services.legal.title}
              </h3>
              <p className="text-[#546A76] leading-relaxed">
                {t.services.legal.desc}
              </p>
            </div>

            {/* Educational Programs */}
            <div 
              data-testid="service-education"
              className="bg-white rounded-3xl p-8 border border-[#E2E8F0] card-hover"
            >
              <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-6">
                <BookOpen className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h3 className="font-serif text-xl font-bold text-[#1A252C] mb-4">
                {t.services.education.title}
              </h3>
              <p className="text-[#546A76] leading-relaxed">
                {t.services.education.desc}
              </p>
            </div>

            {/* Social Reintegration */}
            <div 
              data-testid="service-reintegration"
              className="bg-white rounded-3xl p-8 border border-[#E2E8F0] card-hover"
            >
              <div className="w-14 h-14 rounded-2xl bg-[#4A708B]/10 flex items-center justify-center mb-6">
                <Briefcase className="w-7 h-7 text-[#4A708B]" />
              </div>
              <h3 className="font-serif text-xl font-bold text-[#1A252C] mb-4">
                {t.services.reintegration.title}
              </h3>
              <p className="text-[#546A76] leading-relaxed">
                {t.services.reintegration.desc}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Help CTA */}
      <section className="py-24 bg-[#FAFAFA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-3xl p-12 soft-shadow">
            <div className="w-20 h-20 mx-auto rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-8">
              <Phone className="w-10 h-10 text-[#D4AF37]" />
            </div>
            <h2 className="font-serif text-3xl font-bold text-[#1A252C] mb-4">
              {t.hero.helpBtn}
            </h2>
            <p className="text-[#546A76] text-lg mb-8 max-w-xl mx-auto">
              {t.contact.reassurance}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" data-testid="services-contact-btn">
                <Button className="primary-btn rounded-full px-8 py-6">
                  {t.nav.contact}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="mailto:info@fondationenat.org">
                <Button 
                  variant="outline"
                  className="rounded-full px-8 py-6 border-[#4A708B] text-[#4A708B] hover:bg-[#4A708B] hover:text-white"
                >
                  info@fondationenat.org
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
